import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

import elementsJsonData from "../../../assets/traitInfo/elements.json";
import patternsJsonData from "../../../assets/traitInfo/patterns.json";

import ModalWrapper from "@/components/shared/ModalWrapper/ModalWrapper";
import {
  deleteResponseCollectionDocument,
  setResponseCollectionDocument,
  traitAssessmentTitleMap,
  useGetTraitTraitAssessmentItemResponses,
  useGetTraitTraitAssessmentItems,
} from "../utils";
import {
  TraitsAssessmentIntroStepOne,
  TraitsAssessmentIntroStepTwo,
} from "./TraitsAssessmentIntroSteps";
import { ExtendedPattern, ExtendedScale } from "@/models/sharedModels";
import TraitAssessmentItemsView from "./TraitAssessmentItemsView";
import {
  ItemResponse,
  ScheduledAssessment,
  TraitAssessmentResult,
} from "@dimensional-engineering/dimensional-models";
import { callFirebaseFunctions } from "@/_firebase/callFirebaseFunctions";
import { LoadingScreenWithMessages } from "@/components/assessments-new/views/AsessmentOutroScreen/loadingScreen/LoadingScreen";
import { useAuth } from "@/context/AuthContext";
import { useModalContext } from "@/context/ModalContext";
import TraitAssessmentOutro from "../TraitAssessmentOutro/TraitAssessmentOutro";
import { Mixpanel } from "@/helpers/mixpanel";

type Props = {
  type: "element" | "pattern";
  slug: string;
  hidden: boolean;
  elementName: "TraitsAssessmentModal";
  isPremium?: boolean;
};

export default function TraitsAssessmentModal(props: Props) {
  const { setModalComponent, removeModalByName } = useModalContext();

  const { user } = useAuth();

  const scheduledAssessment = useSelector(
    (state: RootState) => state.scheduledAssessments
  ).scheduledAssessments.find(
    (a) => a.assessmentSlug === props.slug
  ) as ScheduledAssessment;

  const items = useGetTraitTraitAssessmentItems(props.type, props.slug);
  const responses = useGetTraitTraitAssessmentItemResponses({
    scheduledAssessment: scheduledAssessment,
  });

  useEffect(() => {
    setResponseCollectionDocument(scheduledAssessment, user?.uid);

    Mixpanel?.track(
      "Assessment loaded",
      {
        type: "trait assessment",
        slug: props.slug,
        trigger: "system check-in dashboard component",
      },
      user?.uid
    );
  }, []);

  const [step, setStep] = useState<number>(0);

  const element = elementsJsonData.find((e) => e.slug === props.slug) as
    | ExtendedScale
    | undefined;
  const pattern = patternsJsonData.find((p) => p.slug === props.slug) as
    | ExtendedPattern
    | undefined;

  const name =
    props.type === "element" ? element?.alias ?? element?.name : pattern?.name;

  const subTitleMap: Record<number, string | undefined> = {
    0: props.hidden ? "Unknown trait" : name,
    1: "Instructions",
    2: props.hidden ? "Unknown trait" : name,
  };

  async function handleFinish(itemResponses: ItemResponse[]) {
    Mixpanel?.track(
      "Assessment completed",
      {
        type: "trait assessment",
        slug: props.slug,
      },
      user?.uid
    );

    const call = callFirebaseFunctions("computeTraitAssessmentResult");
    const payload = {
      itemResponses: JSON.stringify(itemResponses),
      traitIdentifier: JSON.stringify({
        slug: props.slug,
        type: props.type,
      }),
      scheduleIdentifier: scheduledAssessment.scheduleIdentifier,
    };

    setStep(3);
    return await call(payload)
      .then((res) => {
        if (!props.isPremium) {
          deleteResponseCollectionDocument(scheduledAssessment, user?.uid);
        }
        const data = res.data as {
          traitAssessmentResult: TraitAssessmentResult;
        };
        setModalComponent(
          <TraitAssessmentOutro
            traitAssessmentResult={data.traitAssessmentResult}
            type={props.type}
            slug={props.slug}
            isPremium={props.isPremium}
            source="assessment completion"
          />
        );
        return removeModalByName("TraitsAssessmentModal");
      })
      .catch((err) => {
        return removeModalByName("TraitsAssessmentModal");
      });
  }

  const stepMap: Record<number, JSX.Element> = {
    0: (
      <TraitsAssessmentIntroStepOne
        slug={props.slug}
        name={name}
        hidden={props.hidden}
        isPremium={props.isPremium}
        type={props.type}
        onContinue={() => setStep(1)}
        itemLength={items.length}
        trackingFrequency={
          props.type === "element"
            ? element?.trackingFrequency
            : pattern?.trackingFrequency
        }
      />
    ),
    1: (
      <TraitsAssessmentIntroStepTwo
        slug={props.slug}
        name={name}
        hidden={props.hidden}
        isPremium={props.isPremium}
        type={props.type}
        onContinue={() => setStep(2)}
        itemLength={items.length}
        trackingFrequency={
          props.type === "element"
            ? element?.trackingFrequency
            : pattern?.trackingFrequency
        }
      />
    ),
    2: (
      <TraitAssessmentItemsView
        type={props.type}
        slug={props.slug}
        items={items}
        itemResponses={responses}
        scheduledAssessment={scheduledAssessment}
        onFinish={(itemResponses) => {
          handleFinish(itemResponses);
        }}
      />
    ),
    3: (
      <LoadingScreenWithMessages
        messages={[
          "Analyzing your latest responses…",
          "Calculating your updated scores…",
          "Quantifying your changes…",
          "Understanding who you are…",
          "Exploring your personality evolution…",
          "Generating personalized insights…",
        ]}
      />
    ),
  };

  return (
    <ModalWrapper
      title={traitAssessmentTitleMap[step]}
      subTitle={subTitleMap[step]}
      noHeader={step === 3}
      noLine={step === 2}
    >
      {stepMap[step]}
    </ModalWrapper>
  );
}
